import { useRef, useState, useEffect, useCallback } from 'react';
import { CaretUpDown } from '@phosphor-icons/react';

/**
 * Iter24 F3 — Before/After interactive slider.
 * Props:
 *   - beforeUrl: image URL "Para"
 *   - afterUrl: image URL "Pas"
 * Drag the vertical divider to reveal Before/After.
 */
export default function BeforeAfterSlider({ beforeUrl, afterUrl }) {
  const wrapRef = useRef(null);
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);

  const handleMove = useCallback((clientX) => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const next = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    setPct(next);
  }, []);

  const onMouseDown = (e) => { dragging.current = true; handleMove(e.clientX); };
  const onMouseMove = useCallback((e) => { if (dragging.current) handleMove(e.clientX); }, [handleMove]);
  const onMouseUp = useCallback(() => { dragging.current = false; }, []);
  const onTouchStart = (e) => { dragging.current = true; handleMove(e.touches[0].clientX); };
  const onTouchMove = useCallback((e) => { if (dragging.current && e.touches[0]) handleMove(e.touches[0].clientX); }, [handleMove]);
  const onTouchEnd = useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  if (!beforeUrl || !afterUrl) {
    return (
      <div className="aspect-[16/9] rounded-2xl bg-[#0a0a0a] border border-white/5 flex items-center justify-center"
           data-testid="before-after-placeholder">
        <p className="text-xs text-white/40 text-center px-4">Foto para dhe pas — duke ardhur</p>
      </div>
    );
  }

  return (
    <div ref={wrapRef}
         className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-black border border-white/10 select-none"
         onMouseDown={onMouseDown}
         onTouchStart={onTouchStart}
         data-testid="before-after-slider">
      {/* After image (full background) */}
      <img src={afterUrl} alt="Pas" className="absolute inset-0 w-full h-full object-cover pointer-events-none"
           loading="lazy" />
      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none"
           style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
        <img src={beforeUrl} alt="Para" className="w-full h-full object-cover" loading="lazy" />
      </div>
      {/* Labels */}
      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] font-semibold uppercase tracking-wider pointer-events-none">
        Para
      </div>
      <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] font-semibold uppercase tracking-wider pointer-events-none">
        Pas
      </div>
      {/* Divider line */}
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-2xl pointer-events-none"
           style={{ left: `${pct}%` }} />
      {/* Drag handle */}
      <div className="absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-2xl flex items-center justify-center cursor-ew-resize"
           style={{ left: `calc(${pct}% - 20px)` }}
           data-testid="before-after-handle">
        <CaretUpDown size={20} weight="bold" className="text-[#0ABAB5] rotate-90" />
      </div>
    </div>
  );
}
